import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EpisodeInput, ListResponse, PublicEpisode, PublicEpisodeDetail } from '../types';
import { api } from './client';
import { qk } from './keys';

export interface EpisodesListParams {
  programmeId: string;
  locale: string;
  page?: number;
  pageSize?: number;
  publishedOnly?: boolean;
}

export function useEpisodes(params: EpisodesListParams) {
  return useQuery({
    queryKey: qk.episodes(params.programmeId, params),
    enabled: !!params.programmeId,
    queryFn: () =>
      api.get<ListResponse<PublicEpisode>>(`/admin/programmes/${params.programmeId}/episodes`, {
        locale: params.locale,
        page: params.page,
        pageSize: params.pageSize,
        publishedOnly: params.publishedOnly ? '1' : undefined,
      }),
  });
}

export function useEpisode(programmeId: string | null, episodeSlug: string | null, locale: string) {
  return useQuery({
    queryKey: qk.episode(programmeId ?? '', episodeSlug ?? '', locale),
    enabled: !!programmeId && !!episodeSlug,
    queryFn: () =>
      api.get<PublicEpisodeDetail>(
        `/admin/programmes/${programmeId}/episodes/${encodeURIComponent(episodeSlug ?? '')}`,
        { locale },
      ),
  });
}

export function useCreateEpisode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { programmeId: string; input: EpisodeInput }) =>
      api.post(`/admin/programmes/${args.programmeId}/episodes`, args.input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['episodes', vars.programmeId] });
    },
  });
}

export function useUpdateEpisode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { programmeId: string; episodeId: string; input: Partial<EpisodeInput> }) =>
      api.patch(`/admin/programmes/${args.programmeId}/episodes/${args.episodeId}`, args.input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['episodes', vars.programmeId] });
    },
  });
}

export function useDeleteEpisode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { programmeId: string; episodeId: string }) =>
      api.delete(`/admin/programmes/${args.programmeId}/episodes/${args.episodeId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['episodes', vars.programmeId] });
    },
  });
}

export function useBulkDeleteEpisodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ programmeId, ids }: { programmeId: string; ids: string[] }) =>
      api.post<{ deleted: number }>(`/admin/programmes/${programmeId}/episodes/bulk-delete`, { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['episodes'] }),
  });
}
